import { scale, smoothPath, segments } from './svgPath';
import { dayMonth } from '../../pages/class_performance/lib/cpBits';

export interface ClassTrendPoint { week_start: string; avg: number | null }

interface Props {
  data: ClassTrendPoint[];
  /** Dashed reference line (the pass mark). */
  passMark?: number;
  ariaLabel?: string;
}

/** y range for the chart: the design's 30-80 band, widened in 10s when a score or the pass mark falls outside it. */
export function trendDomain(values: number[], passMark: number): [number, number] {
  const all = [...values, passMark].filter((v) => Number.isFinite(v));
  const lo = Math.min(30, Math.floor((Math.min(...all) - 2) / 10) * 10);
  const hi = Math.max(80, Math.ceil((Math.max(...all) + 2) / 10) * 10);
  return [Math.max(0, lo), Math.min(100, hi)];
}

/** Design CP "Weekly average": area under a smooth line, dots, a dashed pass line and a navy bubble on the latest week. */
export default function ClassTrendChart({ data, passMark = 50, ariaLabel = 'Weekly average score trend' }: Props) {
  const values = data.map((d) => d.avg);
  const finite = values.filter((v): v is number => v != null && Number.isFinite(v));
  const [lo, hi] = trendDomain(finite, passMark);
  const x = scale(0, Math.max(1, data.length - 1), 50, 740);
  const y = scale(lo, hi, 200, 20);
  const ticks: number[] = [];
  for (let t = lo; t <= hi; t += 10) ticks.push(t);
  const runs = segments(values);
  const points = runs.flat();
  const last = points[points.length - 1];
  const nTicks = Math.min(data.length, 6);
  const xTicks = nTicks <= 1 ? [0] : Array.from({ length: nTicks }, (_, k) => Math.round((k * (data.length - 1)) / (nTicks - 1)));
  const bubbleY = last ? Math.max(2, y(last.v) - 44) : 0;
  return (
    <svg viewBox="0 0 760 250" className="block h-auto w-full" role="img" aria-label={ariaLabel}>
      {ticks.map((t) => (
        <g key={t}>
          {t !== passMark && <line x1={40} x2={750} y1={y(t)} y2={y(t)} className="stroke-line" />}
          {t > lo && <text x={30} y={y(t) + 4} textAnchor="end" className="fill-muted font-mono text-[11px]">{t}</text>}
        </g>
      ))}
      {runs.filter((r) => r.length > 1).map((run, k) => {
        const line = smoothPath(run.map((p) => [x(p.i), y(p.v)] as [number, number]));
        return <path key={`a${k}`} d={`${line} L${x(run[run.length - 1].i)},200 L${x(run[0].i)},200 Z`} className="fill-slate" fillOpacity={0.12} />;
      })}
      <line x1={40} x2={750} y1={y(passMark)} y2={y(passMark)} className="stroke-accent-ink" strokeWidth={1.5} strokeDasharray="5 5" />
      {runs.filter((r) => r.length > 1).map((run, k) => (
        <path key={`l${k}`} d={smoothPath(run.map((p) => [x(p.i), y(p.v)] as [number, number]))}
          fill="none" className="stroke-slate" strokeWidth={3} strokeLinejoin="round" strokeLinecap="round" />
      ))}
      {points.map((p) => (
        <circle key={p.i} cx={x(p.i)} cy={y(p.v)} r={3.5} className="fill-surface stroke-slate" strokeWidth={2} />
      ))}
      {last && (
        <>
          <circle cx={x(last.i)} cy={y(last.v)} r={7} className="fill-surface stroke-accent" strokeWidth={4} />
          <rect x={x(last.i) - 69} y={bubbleY} width={78} height={28} rx={8} className="fill-navy" />
          <text x={x(last.i) - 30} y={bubbleY + 19} textAnchor="middle" className="fill-white font-mono text-[13px] font-semibold">{last.v.toFixed(1)}%</text>
        </>
      )}
      {xTicks.map((i) => (
        <text key={i} x={x(i)} y={232} textAnchor="middle" className="fill-muted font-mono text-[11px]">{dayMonth(data[i].week_start)}</text>
      ))}
    </svg>
  );
}
